import {
  Zmanim,
  HDate,
  getHolidaysOnDate,
  flags,
  HebrewCalendar,
  CandleLightingEvent,
} from '@hebcal/core';
import { ZmanimData } from '../types';
import { normalizeZmanim } from './zmanimRules';
import { CANDLE_LIGHTING_MINUTES_BEFORE_SUNSET, RABBEINU_TAM_MINUTES_AFTER_SUNSET } from '../config/constants';
import { formatTime, hebcalLocation, hebcalSunsetFromHeight, hebcalTzeit } from './hebcalSun';

const location = hebcalLocation;

function holidayDescEquals(holidays: ReturnType<typeof getHolidaysOnDate>, name: string): boolean {
  return (holidays || []).some((h) => h.getDesc() === name);
}

// Deliberately narrower than Hebcal's MINOR_FAST flag: that flag also
// covers Ta'anit Bechorot (Erev Pesach, only firstborns, usually broken
// via a siyum) and Yom Kippur Katan (Erev Rosh Chodesh, not universally
// observed communally). Neither is a public fast day for schedule/
// calendar purposes on this site, so isFastDay is restricted to the
// four classic minor fasts. Ta'anit Bechorot in particular falls back
// to being an ordinary weekday here unless a shul adds its own entry.
const CLASSIC_MINOR_FASTS = [
  'Tzom Gedaliah',
  "Asara B'Tevet",
  "Ta'anit Esther",
  'Tzom Tammuz',
];

function computeCandleLighting(dateStr: string, hd: HDate): string | undefined {
  const events = HebrewCalendar.calendar({
    start: hd,
    end: hd,
    location,
    il: true,
    candlelighting: true,
    candleLightingMins: CANDLE_LIGHTING_MINUTES_BEFORE_SUNSET,
    useElevation: true, // 40 minutes before sunset from the height, as on the local calendar
  });
  const candle = events.find((ev) => ev instanceof CandleLightingEvent) as
    | CandleLightingEvent
    | undefined;
  if (!candle?.eventTime) return undefined;
  return formatTime(candle.eventTime);
}

/**
 * Compute a full ZmanimData object for any date using Hebcal — offline, free,
 * no API call. Used as a fallback when /zmanimCache has no doc for the date
 * (which today is everything except whatever date the seed script ran on,
 * until the fetchZmanim Cloud Function is deployed).
 */
export function computeZmanimForDate(dateStr: string): ZmanimData {
  return normalizeZmanim(computeRawZmanim(dateStr));
}

function computeRawZmanim(dateStr: string): ZmanimData {
  const date = new Date(`${dateStr}T12:00:00Z`); // noon UTC avoids day-rollover edge cases
  const z = new Zmanim(location, date, false);
  const hd = new HDate(date);
  const holidays = getHolidaysOnDate(hd, true) || [];
  const hasFlag = (bit: number) => holidays.some((h) => (h.getFlags() & bit) !== 0);
  const dow = date.getUTCDay(); // 0=Sun ... 6=Sat

  // Sunrise and the day's hours stay at sea level (as MyZmanim gives them);
  // sunset is from the height, as MyZmanim's SunsetDefault and the local calendar.
  const sunset = hebcalSunsetFromHeight(dateStr);
  const rabbeinuTam = new Date(sunset.getTime() + RABBEINU_TAM_MINUTES_AFTER_SUNSET * 60000);
  const candleLighting = computeCandleLighting(dateStr, hd);

  // Exact holiday titles so "Erev Tish'a B'Av" (which can carry MAJOR_FAST)
  // does not classify as the fast day itself. Observed-date Tisha B'Av
  // (Sunday when postponed) is titled "Tish'a B'Av" on that Sunday.
  const isYomKippur = holidayDescEquals(holidays, 'Yom Kippur');
  const isTishaBav = holidayDescEquals(holidays, "Tish'a B'Av");

  return {
    date: dateStr,
    locationId: 'hebcal-local',
    AlotHashachar: formatTime(z.alotHaShachar72()),
    Sunrise: formatTime(z.sunrise()),
    SunsetDefault: formatTime(sunset),
    Tzet: hebcalTzeit(dateStr),
    TzetRabbeinuTam: formatTime(rabbeinuTam),
    Chatzot: formatTime(z.chatzot()),
    MinchaGedola: formatTime(z.minchaGedola()),
    MinchaKetana: formatTime(z.minchaKetana()),
    PlagHamincha: formatTime(z.plagHaMincha()),
    ShemaGra: formatTime(z.sofZmanShma()),
    ShemaMA: formatTime(z.sofZmanShmaMGA()),
    ...(candleLighting ? { candleLighting } : {}),
    isShabbos: dow === 6,
    isErevShabbos: dow === 5,
    isYomTov: hasFlag(flags.CHAG),
    isErevYomTov: hasFlag(flags.EREV),
    isCholHaMoed: hasFlag(flags.CHOL_HAMOED),
    isRoshChodesh: hasFlag(flags.ROSH_CHODESH),
    isFastDay: CLASSIC_MINOR_FASTS.some((name) => holidayDescEquals(holidays, name)),
    isTishaBav,
    isYomKippur,
    yomTovName: holidays.find((h) => (h.getFlags() & flags.CHAG) !== 0)?.getDesc(),
    parsha: undefined,
    raw: {},
  };
}
